import type { CellClassParams, ColDef, ICellRendererParams, CellStyle } from 'ag-grid-community'

import {
  LineItemTypeCellRenderer,
  SingleProductGroupCellRenderer,
  StatusCellRenderer,
} from '../../../shared/LineItems/components'
import { ActionsCellRenderer, BookTypeCellRenderer, CounterpartyCellRenderer } from '../renderers'
import { valueLabelFormatter } from '../formatters'
import type { InvoiceDetail } from '../../../../../api/useFreight/types'
import {
  getBOLColumnDef,
  getDestinationsColumnDef,
  getErrorColumnDef,
  getOriginsColumnDef,
} from '@/components/Freight/shared'
import { OverrideValueCellRenderer } from '@/components/Freight/shared/LineItems/components/OverrideValueCellRenderer'
import { isDefined } from '@/utils/general'
import { TransactionErrorCellRenderer } from '../renderers/OrderErrorCellRenderer'

// Extend ColDef to include excludeFromExport property
interface ExtendedColDef<T> extends ColDef<T> {
  excludeFromExport?: boolean
}

export interface TransactionDetail {
  id: string
  book_type: 'Cost' | 'Revenue'
  counterparty: string
  status: string
  product_group: string
  product_name: string
  type: string
  subtype: string
  rate: number
  amount: number
  amount_override?: number
  uom: string
  total: number
  total_override?: number
  amount_original?: number
  total_original?: number
  override_by?: string
  override_date?: string
  credit_rebill_metadata?: {
    impact_type?: 'unchanged' | 'impacted' | 'added' | 'removed'
    delta_amount?: number
    delta_total?: number
  }
}

export const byOrderLineItemColumns = (
  isCreditRebillMode = false
): ExtendedColDef<TransactionDetail | InvoiceDetail>[] => [
  {
    headerName: '',
    field: 'book_type',
    maxWidth: 60,
    cellRenderer: BookTypeCellRenderer,
  },
  {
    headerName: 'COUNTERPARTY',
    field: 'counterparty',
    flex: 1,
    cellRenderer: CounterpartyCellRenderer,
  },
  {
    headerName: 'STATUS',
    field: 'status',
    filter: true,
    sortable: true,
    enableRowGroup: true,
    valueFormatter: valueLabelFormatter,
    filterParams: {
      valueFormatter: valueLabelFormatter,
    },
    flex: 1.5,
    cellRenderer: StatusCellRenderer,
  },
  {
    headerName: 'ERROR',
    field: 'error.type',
    cellRenderer: TransactionErrorCellRenderer,
  },
  {
    headerName: 'ERROR MESSAGE',
    field: 'error.message',
    hide: true,
    suppressColumnsToolPanel: true, // added only for downloading purposes
  },
  {
    ...getOriginsColumnDef(),
  },
  {
    ...getDestinationsColumnDef(),
  },
  {
    ...getBOLColumnDef(),
  },
  {
    headerName: 'PRODUCT GROUP',
    field: 'product_group',
    flex: 0.5,
    cellRenderer: SingleProductGroupCellRenderer,
  },
  {
    headerName: 'PRODUCT NAME',
    field: 'product_name',
    flex: 1,
    cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
      return params.data?.product_name_override ? { background: 'var(--theme-warning-dim)' } : null
    },
    cellRenderer: OverrideValueCellRenderer,
  },
  {
    headerName: 'TYPE',
    field: 'type',
    minWidth: 135,
    cellRenderer: LineItemTypeCellRenderer,
  },
  {
    headerName: 'SUBTYPE',
    field: 'subtype',
    flex: 2,
    cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
      return params.data?.subtype_override ? { background: 'var(--theme-warning-dim)' } : null
    },
    cellRenderer: OverrideValueCellRenderer,
  },
  {
    headerName: 'RATE',
    field: 'rate',
    sortable: true,
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    flex: 1,
    cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
      return isDefined(params.data?.rate_override) ? { background: 'var(--theme-warning-dim)' } : null
    },
    cellRenderer: OverrideValueCellRenderer,
  },
  {
    headerName: 'AMOUNT',
    field: 'amount',
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    flex: 1,
    cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
      return isDefined(params.data?.amount_override) ? { background: 'var(--theme-warning-dim)' } : null
    },
    cellRenderer: OverrideValueCellRenderer,
  },
  {
    headerName: 'UOM',
    field: 'uom',
    flex: 1,
    cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
      return params.data?.uom_override ? { background: 'var(--theme-warning-dim)' } : null
    },
    cellRenderer: OverrideValueCellRenderer,
  },
  {
    headerName: 'TOTAL',
    field: 'total',
    pinned: 'right',
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    width: 120,
    flex: 0,
    cellStyle: (params: CellClassParams<TransactionDetail | InvoiceDetail>): CellStyle | null => {
      return isDefined(params.data?.total_override) ? { background: 'var(--theme-warning-dim)' } : null
    },
    cellRenderer: OverrideValueCellRenderer,
  },
  {
    headerName: '',
    field: 'id',
    sortable: false,
    enableRowGroup: false,
    filter: false,
    pinned: 'right',
    width: 120,
    excludeFromExport: true,
    valueGetter: (params) => (params.data?.status === 'needs_approval' ? 120 : 100),
    cellStyle: { display: 'flex', flex: 1, padding: 0 },
    cellRenderer: (params: ICellRendererParams) => {
      return (
        <ActionsCellRenderer
          data={params.data}
          api={params.api}
          context={params.context}
          isCreditRebillMode={isCreditRebillMode}
        />
      )
    },
  },
]
